<script setup lang="ts">
    import { IInteractionTranscriptionSegment } from '@/js/services/api/interaction/types';
    import SvgIcon from '../../SvgIcon.vue';
    import { useSharedFilterStore } from '@/js/stores/filter';
    import DOMPurify from 'dompurify';
    import { computed } from 'vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, InteractionModulePermissionFlag } from '@/js/consts/permissions';
    import UserAvatar from '../../UserAvatar.vue';
    import { __ } from '@/js/stores/expressions';
    import { useAudioPlayerStore } from '@/js/stores/audioPlayer';
    import { FastForwardIcon } from '@lucide/vue';
    import IconButton from '../../IconButton.vue';
    import { secondsToHHMMSS } from '@/js/utils/dateUtils';

    const { hasPermission } = usePermissions();

    const canSeeSentimentLevelTextSegment = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.SentimentLevelTextSegment
    );

    const props = withDefaults(
        defineProps<{
            agentName: string;
            interactionId: string;
            message: IInteractionTranscriptionSegment;
            isMatch?: boolean;
            highlight?: boolean;
            canListen?: boolean;
        }>(),
        { highlight: true }
    );

    const canStream = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.Streaming
    );

    const audioPlayerStore = useAudioPlayerStore();

    // Only offer "listen from here" when this interaction's audio player is actually
    // present on the page to consume the seek — otherwise the button would do
    // nothing (or, with several players on the page, drive the wrong one).
    const showListenButton = computed(
        () =>
            props.canListen && canStream.value && audioPlayerStore.hasPlayerFor(props.interactionId)
    );

    const listenFromHere = () => {
        audioPlayerStore.seekTo(props.interactionId, props.message.segmentStart);
    };

    const filterStore = useSharedFilterStore();

    const highlightedText = computed(() => {
        const words = filterStore.all?.keywordFilter?.words ?? [];
        let text = props.message.text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');

        if (!props.highlight || words.length === 0) return text;

        const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
        text = text.replace(pattern, '<mark>$1</mark>');

        return DOMPurify.sanitize(text, { ALLOWED_TAGS: ['mark'] });
    });

    const userAvatarLabel = computed(() => {
        if (props.message.speaker === 'Agent') {
            return props.agentName.substring(0, 1) || __('agent');
        }

        return __('customer');
    });

    const isAgent = computed(() => props.message.speaker === 'Agent');

    // The speaker sets the bubble's base color and corner; isMatch (search
    // hit or playing segment) layers a ring on top instead of replacing it.
    const bubbleClasses = computed(() => {
        const base = isAgent.value
            ? 'bg-white rounded-br-[4px]'
            : 'bg-gray-extralight rounded-bl-[4px]';
        const border = props.isMatch
            ? 'border border-transparent ring-1 ring-gray-dark'
            : isAgent.value
              ? 'border border-gray-light'
              : 'border border-transparent';

        return `${base} ${border}`;
    });
</script>
<template>
    <div
        class="group/message flex gap-3 items-start"
        :class="isAgent ? 'justify-end' : 'justify-start'"
        :data-segment-id="message.segmentId"
    >
        <div class="w-7 h-7 mt-5 shrink-0" :class="isAgent ? 'order-2' : 'order-0'">
            <SvgIcon
                v-if="canSeeSentimentLevelTextSegment"
                :name="
                    'sentiment-' + (message.segmentSentiment?.toLowerCase() ?? 'unknown') + '.svg'
                "
                class="stroke-none"
            />
            <UserAvatar
                v-else
                :label="userAvatarLabel"
                size="sm"
                :color="isAgent ? 'avocado' : 'plum'"
            />
        </div>
        <div class="min-w-0 max-w-2/3">
            <div
                class="text-caption-bold flex items-baseline gap-2 mb-1.5"
                :class="isAgent ? 'justify-end' : 'justify-start'"
            >
                <span>{{ isAgent ? agentName : __('customer') }}</span>
                <span class="text-caption font-normal text-gray-medium">
                    {{ secondsToHHMMSS(message.segmentStart) }}
                </span>
            </div>
            <div class="rounded-lg px-4 py-3 relative" :class="bubbleClasses">
                <!-- eslint-disable vue/no-v-html -- sanitized with DOMPurify -->
                <p
                    class="text-body"
                    :class="showListenButton ? 'pr-4' : ''"
                    v-html="highlightedText"
                />
                <!-- eslint-enable vue/no-v-html -->
                <div class="absolute top-0 right-0">
                    <IconButton
                        v-if="showListenButton"
                        area-size="h-6 w-8"
                        class="order-1 opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100"
                        :tooltip="__('transcription-listen-from-here')"
                        @click="listenFromHere"
                    >
                        <FastForwardIcon class="size-4" />
                    </IconButton>
                </div>
            </div>
        </div>
    </div>
</template>
<style scoped>
    :deep(mark) {
        background-color: var(--color-primary-lime);
        color: inherit;
        border-radius: 3px;
        padding: 1px 3px;
        margin: -1px 0;
    }
</style>
